import type { DarijaEntry } from '../../domain/darija'
import { createDarijaLexicon, DEFAULT_DARIJA_ENTRIES } from '../../domain/darija'
import { validateConversionRate } from '../../domain/currency'
import type { ConflictHistory, LocalEntity } from '../../domain/sync'
import { tripDateKey } from '../../domain/tripTime'
import type { CaravanDb } from './db'
import { LocalRepository } from './repository'

export type ConversionPreference = {
  memberId: string
  tripId: string
  madPerEur: number
  updatedAt: string
  syncState: LocalEntity['syncState']
  version: number
}

export type AnecdoteCoordinates = { latitude: number; longitude: number }

export type AnecdoteInput = {
  tripId: string
  memberId: string
  authorDisplayName: string
  productDay: string
  text: string
  place: string
  coordinates: AnecdoteCoordinates | null
}

export type Anecdote = AnecdoteInput & {
  id: string
  createdAtDevice: string
  updatedAtServer: string | null
  version: number
  syncState: LocalEntity['syncState']
  lastMutationId: string
}

export type AnecdotePreviousVersion = {
  text: string
  place: string
  productDay: string
  recordedAt: string
  replacingMutationId: string
}

const defaultRate = 10.8

function textField(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

function coordinatesField(value: unknown): AnecdoteCoordinates | null {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Record<string, unknown>
  if (typeof candidate.latitude !== 'number' || typeof candidate.longitude !== 'number') return null
  return { latitude: candidate.latitude, longitude: candidate.longitude }
}

function fromAnecdoteEntity(entity: LocalEntity): Anecdote {
  return {
    id: entity.id,
    tripId: entity.tripId,
    memberId: textField(entity.payload.memberId ?? entity.payload.authorMemberId),
    authorDisplayName: textField(entity.payload.authorDisplayName),
    productDay: textField(entity.payload.productDay) || tripDateKey(new Date(entity.createdAtDevice)),
    text: textField(entity.payload.text),
    place: textField(entity.payload.place),
    coordinates: coordinatesField(entity.payload.coordinates),
    createdAtDevice: entity.createdAtDevice,
    updatedAtServer: entity.updatedAtServer,
    version: entity.version,
    syncState: entity.syncState,
    lastMutationId: entity.lastMutationId,
  }
}

function anecdotePayload(input: AnecdoteInput): Record<string, unknown> {
  const text = input.text.trim()
  if (text.length < 1 || text.length > 500) throw new Error('L’anecdote doit contenir entre 1 et 500 caractères.')
  if (input.authorDisplayName.trim().length === 0) throw new Error('L’auteur de l’anecdote est requis.')
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(input.productDay)) throw new Error('La date de l’anecdote est invalide.')
  if (input.place.trim().length > 120) throw new Error('Le lieu doit rester sous 120 caractères.')
  if (input.coordinates) {
    if (
      !Number.isFinite(input.coordinates.latitude) || Math.abs(input.coordinates.latitude) > 90 ||
      !Number.isFinite(input.coordinates.longitude) || Math.abs(input.coordinates.longitude) > 180
    ) throw new Error('Les coordonnées de l’anecdote sont invalides.')
  }
  return {
    memberId: input.memberId,
    authorMemberId: input.memberId,
    authorDisplayName: input.authorDisplayName.trim(),
    productDay: input.productDay,
    text,
    place: input.place.trim(),
    coordinates: input.coordinates ? { ...input.coordinates } : null,
  }
}

export class PracticalRepository {
  readonly #db: CaravanDb
  readonly #local: LocalRepository

  constructor(db: CaravanDb) {
    this.#db = db
    this.#local = new LocalRepository(db)
  }

  async loadDarijaEntries(): Promise<readonly DarijaEntry[]> {
    try {
      const response = await fetch('/demo/darija.json', { cache: 'force-cache' })
      if (!response.ok) throw new Error('Seed darija indisponible.')
      return createDarijaLexicon(await response.json() as DarijaEntry[])
    } catch {
      return DEFAULT_DARIJA_ENTRIES
    }
  }

  async getConversionPreference(memberId: string, tripId: string): Promise<ConversionPreference> {
    const key = `conversion_preference:${memberId}`
    let entity = await this.#db.entities.get(key)
    if (!entity) {
      const updatedAt = '2026-09-05T00:00:00.000Z'
      entity = {
        key,
        id: memberId,
        tripId,
        entityType: 'conversion_preference',
        payload: { memberId, madPerEur: defaultRate, updatedAt },
        createdAtDevice: updatedAt,
        updatedAtDevice: updatedAt,
        updatedAtServer: updatedAt,
        version: 1,
        deletedAt: null,
        lastMutationId: `seed:conversion:${memberId}`,
        syncState: 'synced',
        lastError: null,
      }
      await this.#db.entities.put(entity)
    }
    const storedRate = typeof entity.payload.madPerEur === 'number' ? entity.payload.madPerEur : defaultRate
    let madPerEur = defaultRate
    try { madPerEur = validateConversionRate(storedRate) } catch { /* Fall back to the bundled offline snapshot. */ }
    return {
      memberId,
      tripId,
      madPerEur,
      updatedAt: textField(entity.payload.updatedAt) || entity.updatedAtDevice,
      syncState: entity.syncState,
      version: entity.version,
    }
  }

  async saveConversionRate(memberId: string, tripId: string, madPerEur: number): Promise<ConversionPreference> {
    const validRate = validateConversionRate(madPerEur)
    const previous = await this.getConversionPreference(memberId, tripId)
    const updatedAt = new Date().toISOString()
    const entity = await this.#local.mutate({
      mutationId: crypto.randomUUID(),
      entityType: 'conversion_preference',
      entityId: memberId,
      tripId,
      operation: 'upsert',
      baseVersion: previous.version,
      payload: { memberId, madPerEur: validRate, updatedAt },
      createdAtDevice: updatedAt,
    })
    return {
      memberId, tripId, madPerEur: validRate, updatedAt,
      syncState: entity.syncState,
      version: entity.version,
    }
  }

  async createAnecdote(input: AnecdoteInput): Promise<Anecdote> {
    const id = crypto.randomUUID()
    const createdAtDevice = new Date().toISOString()
    const entity = await this.#local.mutate({
      mutationId: crypto.randomUUID(),
      entityType: 'anecdote',
      entityId: id,
      tripId: input.tripId,
      operation: 'create',
      baseVersion: 0,
      payload: anecdotePayload(input),
      createdAtDevice,
    })
    return fromAnecdoteEntity(entity)
  }

  async updateAnecdote(id: string, input: AnecdoteInput, baseVersion: number): Promise<Anecdote> {
    const entity = await this.#local.mutate({
      mutationId: crypto.randomUUID(),
      entityType: 'anecdote',
      entityId: id,
      tripId: input.tripId,
      operation: 'update',
      baseVersion,
      payload: anecdotePayload(input),
      createdAtDevice: new Date().toISOString(),
    })
    return fromAnecdoteEntity(entity)
  }

  async listAnecdotes(tripId: string): Promise<Anecdote[]> {
    const entities = await this.#db.entities
      .where('entityType')
      .equals('anecdote')
      .filter((entity) => entity.tripId === tripId && entity.deletedAt === null)
      .toArray()
    return entities.map(fromAnecdoteEntity).sort((left, right) =>
      right.createdAtDevice.localeCompare(left.createdAtDevice) || right.id.localeCompare(left.id),
    )
  }

  async getAnecdote(id: string): Promise<Anecdote | null> {
    const entity = await this.#db.entities.get(`anecdote:${id}`)
    return entity?.deletedAt === null ? fromAnecdoteEntity(entity) : null
  }

  async applyServerWinner(
    id: string,
    winner: { mutationId: string; receivedAtServer: string; version: number; payload: Record<string, unknown> },
  ): Promise<void> {
    await this.#db.transaction('rw', this.#db.entities, this.#db.histories, async () => {
      const entity = await this.#db.entities.get(`anecdote:${id}`)
      if (!entity) throw new Error('Anecdote locale introuvable.')
      await this.#db.histories
        .where('entityId')
        .equals(id)
        .filter((history) => history.entityType === 'anecdote')
        .delete()
      const history: ConflictHistory = {
        id: `anecdote:${id}:previous`,
        entityType: 'anecdote',
        entityId: id,
        localPayload: structuredClone(entity.payload),
        serverPayload: structuredClone(winner.payload),
        localVersion: entity.version,
        serverVersion: winner.version,
        recordedAt: winner.receivedAtServer,
        replacingMutationId: winner.mutationId,
      }
      await this.#db.histories.put(history)
      await this.#db.entities.update(entity.key, {
        payload: structuredClone(winner.payload),
        version: winner.version,
        updatedAtServer: winner.receivedAtServer,
        lastMutationId: winner.mutationId,
        syncState: 'synced',
        lastError: null,
      })
    })
  }

  async getPreviousVersions(id: string): Promise<AnecdotePreviousVersion[]> {
    const rows = await this.#db.histories
      .where('entityId')
      .equals(id)
      .filter((history) => history.entityType === 'anecdote')
      .toArray()
    return rows.map((history) => ({
      text: textField(history.localPayload.text),
      place: textField(history.localPayload.place),
      productDay: textField(history.localPayload.productDay),
      recordedAt: history.recordedAt,
      replacingMutationId: history.replacingMutationId,
    }))
  }
}
